/**
 * schedule — pure helpers for detecting time overlaps between sessions.
 * Used to warn attendees when they bookmark two sessions that clash.
 */
import type { Session } from '@conference/shared';

export const SESSION_TYPE_LABELS: Record<string, string> = {
  keynote: 'Keynote',
  talk: 'Presentation',
  workshop: 'Workshop',
  panel: 'Panel',
  poster: 'Poster',
  break: 'Break',
  networking: 'Networking',
  social: 'Event',
};

type AgendaFilterLabel = { type: string; label?: string | null };

export function sessionTypeLabel(type: string | null | undefined, agendaFilters?: readonly AgendaFilterLabel[]): string {
  const normalizedType = type?.toLowerCase() ?? '';
  const label = agendaFilters?.find((filter) => filter.type.toLowerCase() === normalizedType)?.label?.trim();
  return label || SESSION_TYPE_LABELS[normalizedType] || 'Session';
}

function toMinutes(date: string, time: string): number {
  const [h = 0, m = 0] = time.split(':').map(Number);
  // Days are relative within an event; encode date ordinal so cross-day
  // sessions never register as overlapping.
  const dayOrd = Number(date.replace(/-/g, '')) || 0;
  return dayOrd * 24 * 60 + h * 60 + m;
}

/** True if two sessions occupy overlapping wall-clock time on the same day. */
export function sessionsOverlap(a: Session, b: Session): boolean {
  if (a.date !== b.date) return false;
  const aStart = toMinutes(a.date, a.start_time);
  const aEnd = toMinutes(a.date, a.end_time);
  const bStart = toMinutes(b.date, b.start_time);
  const bEnd = toMinutes(b.date, b.end_time);
  return aStart < bEnd && bStart < aEnd;
}

/**
 * Given a set of (bookmarked) sessions, return a map of sessionId → the other
 * sessions it clashes with. Only sessions that clash appear as keys.
 */
export function findConflicts(sessions: Session[]): Map<string, Session[]> {
  const conflicts = new Map<string, Session[]>();
  for (let i = 0; i < sessions.length; i++) {
    for (let j = i + 1; j < sessions.length; j++) {
      const a = sessions[i];
      const b = sessions[j];
      if (sessionsOverlap(a, b)) {
        (conflicts.get(a.id) ?? conflicts.set(a.id, []).get(a.id)!).push(b);
        (conflicts.get(b.id) ?? conflicts.set(b.id, []).get(b.id)!).push(a);
      }
    }
  }
  return conflicts;
}

/** Set of session ids that clash with at least one other session in the list. */
export function conflictingIds(sessions: Session[]): Set<string> {
  return new Set(findConflicts(sessions).keys());
}
